<template>
    <div :class="$attrs.class">
        <Popper
            :placement="popperPlacement"
            arrow
            block
            disable-pointer-events
            :show="!!errorMessage && isFocused"
            :content="errorMessage"
            class="text-14">
            <div class="form-input overflow-hidden">
                <textarea
                    :id="fieldId"
                    ref="inputField"
                    v-autofocus="autofocus"
                    v-prohibit-zoom
                    class="w-full"
                    :class="{ 'resize-none': !resizeable }"
                    autocomplete="off"
                    autocorrect="off"
                    autocapitalize="off"
                    spellcheck="false"
                    data-lpignore="true"
                    v-bind="fieldAttrs"
                    tabindex="0"
                    :placeholder="placeholder"
                    :name="name"
                    :type="type"
                    :value="inputValue"
                    :has-error="!!errorMessage"
                    :has-label="!!label"
                    :has-value="inputValue === 0 || !!inputValue"
                    :disabled="disabled"
                    v-on="validationListeners"
                    @focus="isFocused = true">
                </textarea>

                <label v-if="label"
                       :for="fieldId"
                       :class="{ active: inputValue === 0 || !!inputValue }"
                       class="truncate">
                    {{ label }}
                </label>

                <InputClear
                    v-else-if="!disabled && !readonly && inputClear && !!inputValue"
                    class="input-clear"
                    @clear="handleClear"/>
            </div>
        </Popper>
    </div>
</template>

<script lang="ts">
import { computed, defineComponent, PropType, ref } from 'vue';
import { useField } from 'vee-validate';

export default defineComponent({
    name: 'InputTextArea',
    inheritAttrs: false,
    props: {
        type: {
            type: String,
            default: 'text',
        },
        value: {
            type: [String, Number],
            default: '',
        },
        // eslint-disable-next-line vue/require-default-prop
        modelValue: {
            type: null,
        },
        name: {
            type: String,
            required: true,
        },
        label: {
            type: String,
            default: '',
        },
        placeholder: {
            type: String,
            default: undefined,
        },
        popperPlacement: {
            type: String,
            default: 'top',
        },
        icon: {
            type: String,
            required: false,
        },
        id: {
            type: String,
            required: false,
            default: null,
        },
        autofocus: {
            type: Boolean,
            required: false,
            default: false,
        },
        resizeable: {
            type: Boolean,
            required: false,
            default: true,
        },
        inputClear: {
            type: Boolean,
            required: false,
            default: true,
        },
        disabled: {
            type: Boolean,
            required: false,
            default: false,
        },
        readonly: {
            type: Boolean,
            required: false,
            default: false,
        },
        applySmileys: {
            type: Boolean,
            required: false,
            default: false,
        },
        validationBehaviour: {
            type: String,
            default: 'aggressive',
            validate: (value: string): boolean =>
                ['aggressive', 'eager'].includes(value),
        },
        rules: {
            type: Object as PropType<{ [rule: string]: any }>,
            required: false,
        },
    },
    setup(props, { attrs, emit }) {
        const isFocused = ref(false);
        const inputField = ref<HTMLInputElement | null>(null);

        const {
            value: inputValue,
            errorMessage,
        } = useField<string | number>(props.name, props.rules, {
            initialValue: props.value || props.modelValue,
            validateOnValueUpdate: true,
            validateOnMount: true,
            syncVModel: true,
            label: props.label,
        });

        const fieldId = computed(() => {
            return props.id || props.name + Math.random();
        });

        const fieldAttrs = computed(() => {
            const fieldAttrs = {
                ...attrs,
                class: '',
            };
            return fieldAttrs;
        });

        const focusInput = () => {
            if (inputField.value) {
                inputField.value.focus();

                // Stupid hack to place mouse at end of input value
                const val = inputField.value.value;
                if (val) {
                    inputField.value.value = '';
                    inputField.value.value = val;
                }
            }
        };

        const handleClear = () => {
            inputValue.value = '';
            emit('clear');
            focusInput();
        };

        const formatText = (text: string) => {
            if (props.applySmileys && text?.length > 1) {
                // Replace :) or :-) with smiley
                text = text.replace(/:-?\)/g, '😊');
                // Replace :D or :-D with big smiley
                text = text.replace(/:-?D/g, '😁');
                // Replace :( or :-( with sad smiley
                text = text.replace(/:-?\(/g, '😔');
                // Replace :P or :-P with tongue-out smiley
                text = text.replace(/:-?P/g, '😋');
                // Replace x) or x-) with wink smiley
                text = text.replace(/x(-?D|-\))/g, '😝');
            }

            return text;
        };

        const getValue = (evt) => {
            const value = evt.target.value;
            return formatText(value);
        };

        const handleInputEvent = (evt) => {
            isFocused.value = true;

            if (evt && !props.readonly) {
                inputValue.value = getValue(evt);
            }
        };

        const handleChangeEvent = (evt) => {
            isFocused.value = true;

            if (evt && !props.readonly) {
                inputValue.value = getValue(evt);
            }
        };

        const handleBlurEvent = () => {
            isFocused.value = false;
        };

        const validationListeners = computed(() => {
            // if no current errors use lazy validation handlers
            const listeners = {
                blur: handleBlurEvent,
                change: handleChangeEvent,
                input: handleInputEvent,
            };

            // if aggressive mode or current errors, replace with eager handlers to validate changes immediately
            if (props.validationBehaviour === 'aggressive' || errorMessage.value) {
                listeners.input = handleChangeEvent;
            }

            return listeners;
        });

        return {
            inputField,
            validationListeners,
            errorMessage,
            inputValue,
            fieldId,
            focusInput,
            handleClear,
            fieldAttrs,
            isFocused,
        };
    },
});
</script>

<style lang="scss" scoped>
@use "@/styling/6-forms/forms.scss";

.color-picker {
    cursor: pointer;

    &:hover {
        opacity: 0.8;
    }
}
</style>
